import { useId, useMemo, useState } from 'react';
import { Alert, Button, Chip, ErrorBanner } from '@zarya/ui';
import { IdentityStateBadge } from '@app/main/Identities';
import { useIdentitiesForPolicy } from './Policies.api';
import { reachText, selectionText, type Identity } from './PolicyModel';
import { t } from '@app/i18n';
import { withEmphasis } from '@app/i18n/emphasis';
import s from './Policies.module.css';

export interface PolicyIdentitiesTabProps {
  /** Ids selecionados. */
  valor: string[];
  onChange: (ids: string[]) => void;
  editable: boolean;
  /** O atalho «todas» da P6.2 — o bloco de concessão é ou a lista ou o atalho. */
  allIdentities: boolean;
  onAllIdentitiesChange: (todas: boolean) => void;
}

/**
 * Aba **Identities** do drawer (T12).
 *
 * (Até DEF-32, 04/09, esta grade era reaproveitada pela seção «A2A concedido» da
 * aba Capabilities — daí os antigos `titulo`/`placeholder` e o atalho opcional.
 * A seção saiu: a regra não concede A2A; quem diz que um agente invoca outro é
 * o contrato da identidade, em Identity Access › Outgoing.)
 *
 * A MOLDURA É DE CONCESSÃO, NÃO DE ESCOPO (03/09, Human × Identity Access)
 *
 *   O dado é o mesmo da P6.2 (`all_identities`/lista) — mudou a pergunta. Antes
 *   era «por qual agente esta política vale»; agora é «quais identidades esta
 *   regra libera para a pessoa». Marcar uma identidade aqui dá acesso a ela **à
 *   pessoa**, e não concede capability nenhuma **à identidade**: o que a
 *   identidade alcança se define em Identity Access. As duas concessões citam a
 *   mesma identidade e são independentes — o acesso efetivo é a **interseção**.
 *
 * SELEÇÃO EM MASSA AGE SÓ NO QUE O FILTRO MOSTRA
 *
 *   «Selecionar filtradas» e «Limpar filtradas» agem sobre as linhas visíveis, e
 *   a seleção fora do filtro é preservada — marcar (ou desmarcar) às cegas o que
 *   não se vê é como se concede, ou se tira, acesso sem querer.
 *
 * O ATALHO «TODAS» E A CONFIRMAÇÃO (P6.2, guarda de visibilidade 1)
 *
 *   Ligar «todas» não é escolher muitas identidades: é escolher as que existem
 *   **e as que vierem**. Uma identidade aprovada amanhã já nasce coberta, sem
 *   ninguém abrir esta tela. Por isso ligar pede confirmação e mostra o alcance
 *   em número («hoje: N identidades · as futuras herdam»): o custo de um clique a
 *   mais é baixo, o de uma herança que ninguém viu não é.
 *
 *   Voltar para «somente estas» não pede nada — tirar acesso não precisa de
 *   cerimônia.
 */
export function PolicyIdentitiesTab({
  valor,
  onChange,
  editable,
  allIdentities,
  onAllIdentitiesChange,
}: PolicyIdentitiesTabProps) {
  const [search, setBusca] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const identidades = useIdentitiesForPolicy(true);
  // O par de radios precisa de um `name` próprio por instância: dois drawers na
  // mesma página compartilhariam o grupo e um apagaria a escolha do outro.
  const groupId = useId();

  const all = useMemo(() => identidades.data?.items ?? [], [identidades.data]);
  // Só as ATRIBUÍDAS entram no alcance: identidade sem slug não tem o que
  // publicar no PDP, e contá-la prometeria alcance que não existe (FR-089).
  const atribuidas = useMemo(() => all.filter((i: Identity) => Boolean(i.slug)).length, [all]);
  const todas = allIdentities;
  const filtering = search.trim().length > 0;
  const visiveis = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((identidade: Identity) =>
      [
        identidade.slug,
        // A busca acha pelo que a linha MOSTRA: sem isto, digitar o nome que
        // está na tela não achava a identidade ainda não atribuída (DEF-06).
        identidade.image_basename,
        identidade.role,
        identidade.specialty,
        identidade.description,
      ]
        .filter(Boolean)
        .some((campo) => String(campo).toLowerCase().includes(q)),
    );
  }, [all, search]);

  const marcada = (id: string) => valor.includes(id);

  function alternar(id: string) {
    onChange(marcada(id) ? valor.filter((other) => other !== id) : [...valor, id]);
  }

  /** Marca **as filtradas**, somando à seleção que está fora do filtro. */
  function selectFiltered() {
    onChange(Array.from(new Set([...valor, ...visiveis.map((identidade) => identidade.id)])));
  }

  /** Desmarca **as filtradas**, e só elas: o que o filtro esconde continua marcado. */
  function clearFiltered() {
    const noFilter = new Set(visiveis.map((identidade) => identidade.id));
    onChange(valor.filter((id) => !noFilter.has(id)));
  }

  /** O ☑ do cabeçalho marca (ou desmarca) a página filtrada, nunca o catálogo. */
  const allFilteredMarked =
    visiveis.length > 0 && visiveis.every((identidade) => marcada(identidade.id));

  return (
    <div className={s.aba}>
      <fieldset className={s.grant}>
        <legend className={s.legenda}>{t('humanAccess.identities.legend')}</legend>
        <label className={[s.grantOption, todas ? s.grantOptionOn : ''].filter(Boolean).join(' ')}>
          <input
            type="radio"
            name={`${groupId}-grant`}
            checked={todas}
            disabled={!editable}
            onChange={() => {
              // Ligar não aplica direto: mostra o alcance e espera o «Entendi,
              // usar todas». É a confirmação que a P6.2 exige.
              if (!todas) setConfirmando(true);
            }}
          />
          <b>{t('humanAccess.identities.all.option')}</b>
          <small className={s.grantOptionHelp}>{t('humanAccess.identities.all.help')}</small>
        </label>
        <label className={[s.grantOption, todas ? '' : s.grantOptionOn].filter(Boolean).join(' ')}>
          <input
            type="radio"
            name={`${groupId}-grant`}
            checked={!todas}
            disabled={!editable}
            onChange={() => {
              setConfirmando(false);
              onAllIdentitiesChange(false);
            }}
          />
          <b>{t('humanAccess.identities.only.option')}</b>
          <small className={s.grantOptionHelp}>{t('humanAccess.identities.only.help')}</small>
        </label>
      </fieldset>

      <p className={s.grantHelp} data-testid="grant-help">
        {withEmphasis(t('humanAccess.identities.grantHelp'))}
      </p>

      {confirmando && !todas ? (
        <Alert tone="warning" title={t('humanAccess.identities.confirm.title')}>
          <p>{t('humanAccess.identities.confirm.body', { reach: reachText(atribuidas) })}</p>
          <p>{withEmphasis(t('humanAccess.identities.confirm.needsCondition'))}</p>
          <div className={s.acoes}>
            <Button
              size="sm"
              onClick={() => {
                onAllIdentitiesChange(true);
                onChange([]);
                setConfirmando(false);
              }}
            >
              {t('humanAccess.identities.confirm.useAll')}
            </Button>
            <Button variant="soft" size="sm" onClick={() => setConfirmando(false)}>
              {t('humanAccess.identities.confirm.cancel')}
            </Button>
          </div>
        </Alert>
      ) : null}

      {todas ? (
        <Alert tone="info" title={t('humanAccess.identities.all.title')}>
          <p>{t('humanAccess.identities.all.body', { reach: reachText(atribuidas) })}</p>
        </Alert>
      ) : null}

      <div className={s.abaBarra} hidden={todas} data-testid="identities-bar">
        <input
          type="search"
          className={s.busca}
          value={search}
          placeholder={t('humanAccess.identities.searchPlaceholder')}
          aria-label={t('humanAccess.identities.searchPlaceholder')}
          onChange={(e) => setBusca(e.target.value)}
        />
        {editable ? (
          <>
            <Button variant="out" size="sm" onClick={selectFiltered}>
              {t('humanAccess.identities.selectFiltered', { n: visiveis.length })}
            </Button>
            <Button variant="soft" size="sm" onClick={clearFiltered}>
              {t('humanAccess.identities.clearFiltered')}
            </Button>
          </>
        ) : null}
        <span className={s.nota}>
          {selectionText(valor.length, all.length, filtering ? visiveis.length : null)}
        </span>
      </div>

      {identidades.isError ? (
        <ErrorBanner
          error={identidades.error}
          title={t('humanAccess.identities.readError')}
          onRetry={() => void identidades.refetch()}
          hideLink
        />
      ) : null}

      {identidades.isLoading ? (
        <p className={s.vazio}>{t('humanAccess.identities.loading')}</p>
      ) : null}

      {!identidades.isLoading && !identidades.isError && !todas ? (
        <table className={s.grade}>
          <caption className="sr-only">{t('humanAccess.identities.caption')}</caption>
          <thead>
            <tr>
              <th scope="col" className={s.colCheck}>
                <input
                  type="checkbox"
                  checked={allFilteredMarked}
                  disabled={!editable || visiveis.length === 0}
                  aria-label={t('humanAccess.identities.markFiltered')}
                  onChange={() => (allFilteredMarked ? clearFiltered() : selectFiltered())}
                />
              </th>
              <th scope="col">{t('humanAccess.identities.col.identity')}</th>
              <th scope="col">{t('humanAccess.identities.col.role')}</th>
              <th scope="col">{t('humanAccess.identities.col.contract')}</th>
              <th scope="col">{t('humanAccess.identities.col.state')}</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((identidade) => (
              <tr
                key={identidade.id}
                data-testid={`identidade-${identidade.slug ?? identidade.id}`}
              >
                <td className={s.colCheck}>
                  <input
                    type="checkbox"
                    checked={marcada(identidade.id)}
                    disabled={!editable}
                    // O nome acessível é o MESMO que a linha mostra: com o
                    // UUID aqui, quem usa leitor de tela ouvia um id onde a
                    // tela escreve um nome, e nenhum comando de voz alcançava
                    // a linha (parte do DEF-06, do lado da acessibilidade).
                    aria-label={identidade.slug ?? identidade.image_basename ?? identidade.id}
                    onChange={() => alternar(identidade.id)}
                  />
                </td>
                <td>
                  <strong className={s.mono}>
                    {identidade.slug ??
                      identidade.image_basename ??
                      t('humanAccess.identities.noSlug')}
                  </strong>
                  {identidade.description ? (
                    <span className={s.descricao}>{identidade.description}</span>
                  ) : null}
                </td>
                <td>
                  {identidade.role ? (
                    <Chip>{identidade.role}</Chip>
                  ) : (
                    <span className={s.vazio}>{t('humanAccess.identities.dash')}</span>
                  )}
                </td>
                <td className={s.mono}>
                  {identidade.active_contract_version
                    ? `v${identidade.active_contract_version}`
                    : t('humanAccess.identities.dash')}
                </td>
                <td>
                  {/*
                    O rótulo, o tom e o ⓘ do estado vêm do `identityVocab` — a
                    tela mostrava a chave crua (`descoberta_sem_contrato`) e
                    ainda pintava o tom pelo `security_ready`, que é outro dado.
                    Estado de identidade é o mesmo em T4, T5 e aqui.
                  */}
                  <IdentityStateBadge state={identidade.state} tip={false} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {!identidades.isLoading && !identidades.isError && visiveis.length === 0 ? (
        <p className={s.vazio}>{t('humanAccess.identities.empty')}</p>
      ) : null}

      {!todas && valor.length === 0 ? (
        <Alert tone="info" title={t('humanAccess.identities.none.title')}>
          <p>{t('humanAccess.identities.none.body')}</p>
        </Alert>
      ) : null}
    </div>
  );
}
